import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "../api/client";
import { AreaChart, Area, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, Legend } from "recharts";
import ProjectNav from "../components/ProjectNav";

export const fmt = (v: number | null | undefined, prefix = "$") => {
  if (v === null || v === undefined) return "—";
  const abs = Math.abs(v);
  const sign = v < 0 ? "-" : "";
  if (abs >= 1_000_000) return `${sign}${prefix}${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${sign}${prefix}${(abs / 1_000).toFixed(0)}K`;
  return `${sign}${prefix}${abs.toFixed(0)}`;
};

export const cpiColor = (v: number | null | undefined) => {
  if (!v) return "text-gray-400";
  if (v > 1.045) return "text-blue-600";
  if (v >= 0.945) return "text-green-600";
  if (v >= 0.845) return "text-yellow-500";
  return "text-red-600";
};
const cpiDot = (v: number | null | undefined) => {
  if (!v) return "bg-gray-400";
  if (v > 1.045) return "bg-blue-500";
  if (v >= 0.945) return "bg-green-500";
  if (v >= 0.845) return "bg-yellow-400";
  return "bg-red-500";
};
export const varColor = (v: number | null | undefined) => !v ? "text-gray-400" : v < 0 ? "text-red-600" : "text-green-600";
export const statusBadge = (cpi: number | null | undefined) => {
  if (!cpi) return <span className="text-xs text-gray-400">—</span>;
  if (cpi > 1.045) return <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200">Above Target</span>;
  if (cpi >= 0.945) return <span className="text-xs px-2 py-0.5 rounded-full bg-green-50 text-green-700 border border-green-200">On Track</span>;
  if (cpi >= 0.845) return <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-50 text-yellow-700 border border-yellow-200">Watch</span>;
  return <span className="text-xs px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200">Critical</span>;
};

export function MetricModal({ metric, title, subtitle, onClose, financials, metrics, kpiAnalysis, reportId, workPackages }: any) {
  const [modalTab, setModalTab] = useState<"reasoning" | "breakdown">("reasoning");
  const [thresholds, setThresholds] = useState<any>({});
  const [history, setHistory] = useState<any[]>([]);
  const depts = financials?.summary_table || [];
  const [expandedDept, setExpandedDept] = useState<string|null>(null);

  useEffect(() => {
    if (!reportId) return;
    api.get(`/api/reports/${reportId}/config/thresholds`).then(r => setThresholds(r.data)).catch(() => {});
    api.get(`/api/data/evm/history?report_id=${reportId}&limit=6`).then(r => setHistory(r.data?.history || [])).catch(() => {});
  }, [reportId]);

  const pfx       = metric === "spi" ? "spi" : "cpi";
  const greenMin  = thresholds[`${pfx}_green_min`]  ?? 0.945;
  const greenMax  = thresholds[`${pfx}_green_max`]  ?? 1.045;
  const yellowMin = thresholds[`${pfx}_yellow_min`] ?? 0.845;
  const hasThreshold = metric === "cpi" || metric === "spi";

  const getFormula = () => {
    const { bcwp, acwp, bac, eac, cpi, spi, tcpi_eac, cei, mr_offset, mr_remains } = metrics;
    const fmtN = (v: any) => fmt(v, "");
    switch (metric) {
      case "cpi": return [
        "CPI  =  BCWP / ACWP",
        `     =  ${fmt(bcwp)} / ${fmt(acwp)}`,
        `     =  ${cpi ? (cpi * 100).toFixed(1) + "%" : "—"}`,
      ];
      case "spi": {
        const bcws = spi && bcwp ? bcwp / spi : null;
        return [
          "SPI  =  BCWP / BCWS",
          `     =  ${fmt(bcwp)} / ${fmt(bcws)}`,
          `     =  ${spi ? (spi * 100).toFixed(1) + "%" : "—"}`,
        ];
      }
      case "eac": {
        const etc = eac != null && acwp != null ? eac - acwp : null;
        return [
          "EAC  =  ACWP + ETC",
          `     =  ${fmt(acwp)} + ${fmt(etc)}`,
          `     =  ${fmt(eac)}`,
        ];
      }
      case "vac": return [
        "VAC  =  BAC − EAC",
        `     =  ${fmt(bac)} − ${fmt(eac)}`,
        `     =  ${fmt(metrics.vac)}`,
      ];
      case "tcpi": {
        const num = bac != null && bcwp != null ? bac - bcwp : null;
        const den = eac != null && acwp != null ? eac - acwp : null;
        return [
          "TCPI  =  (BAC − BCWP) / (EAC − ACWP)",
          `      =  ${fmt(num)} / ${fmt(den)}`,
          `      =  ${tcpi_eac ? (tcpi_eac * 100).toFixed(1) + "%" : "—"}`,
        ];
      }
      case "cei": return [
        "CEI  =  CPI × SPI",
        `     =  ${cpi?.toFixed(3) ?? "—"} × ${spi?.toFixed(3) ?? "—"}`,
        `     =  ${cei?.toFixed(3) ?? "—"}`,
      ];
      case "bac": return ["BAC  =  Total Budget at Completion", `     =  ${fmt(bac)}`];
      case "mr": return [
        "MR OFFSET   =  CUM RV $",
        "MR REMAINS  =  RESERV $",
        `Offset:    ${fmt(mr_offset)}`,
        `Remaining: ${fmt(mr_remains)}`,
      ];
      default: return [];
    }
  };

  const ThresholdBox = ({ label, range, colorClass, bgClass }: any) => (
    <div className={`rounded-lg px-3 py-2 border ${bgClass}`}>
      <p className={`text-xs font-bold ${colorClass}`}>{label}</p>
      <p className={`text-[11px] ${colorClass} opacity-80`}>{range}</p>
    </div>
  );

  const currentVal = metric === "cpi" ? metrics.cpi : metric === "spi" ? metrics.spi : null;
  const improvementNeeded = currentVal != null && currentVal < greenMin
    ? ((greenMin - currentVal) / currentVal * 100).toFixed(1)
    : null;
  const alreadyGreen = currentVal != null && currentVal >= greenMin && currentVal <= greenMax;

  const renderReasoning = () => {
    const k = kpiAnalysis[metric];
    const factors: string[] = k?.root_causes || k?.drivers || [];
    const { bcwp, acwp, bac, eac, cpi, spi, tcpi_eac, cei, mr_offset, mr_remains } = metrics;

    // Bar chart data — last bar highlighted red
    const fmtBarLabel = (raw: string) => {
      if (!raw) return raw;
      // "2026-06" → "Jun '26"
      const isoMatch = raw.match(/^(\d{4})-(\d{2})$/);
      if (isoMatch) {
        const names = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
        const mNum = parseInt(isoMatch[2]);
        return `${names[mNum - 1]} '${isoMatch[1].slice(2)}`;
      }
      // "Jan 2026" / "JAN 2026" → "Jan '26"
      const wordMatch = raw.match(/^([A-Za-z]+)\s+(\d{4})$/);
      if (wordMatch) {
        return `${wordMatch[1].slice(0,3)} '${wordMatch[2].slice(2)}`;
      }
      return raw;
    };
    // Use quarter (the reporting period) as label — not recorded_at (pipeline run date)
    // Deduplicate: keep last snapshot per unique quarter label
    const seenLabels = new Map<string, {label: string; val: number}>();
    history.forEach((h: any) => {
      const label = fmtBarLabel(h.quarter || h.recorded_at);
      const val = metric === "spi" ? h.spi : h.cpi;
      if (label && val != null && val > 0) seenLabels.set(label, { label, val });
    });
    const historyData = Array.from(seenLabels.values());

    // Custom bar color: last bar red, others gray
    const BarColorCell = (props: any) => {
      const { x, y, width, height, index } = props;
      const isLast = index === historyData.length - 1;
      return <rect x={x} y={y} width={width} height={height} fill={isLast ? "#ef4444" : "#d1d5db"} rx={2} />;
    };

    return (
      <div className="space-y-5">
        <div className="grid grid-cols-2 gap-x-4 gap-y-3">
          {/* LEFT TOP: header + gray box (formula+trend) */}
          <div className="flex flex-col gap-3">

            {/* 1. "{Title} REASONING" header — above gray box */}
            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">{title} REASONING</p>

            {/* 2. Gray box — formula + monthly trend ONLY */}
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 flex-1 flex flex-col gap-4">

              {/* Formula rows */}
              <div>
                {metric === "cpi" && (
                  <div className="space-y-3 text-sm">
                    <div className="flex items-center gap-2">
                      <span className="text-gray-400">≡</span>
                      <span className="text-gray-500">CPI = BCWP ÷ ACWP</span>
                      <span className="text-gray-400 mx-1">→</span>
                      <span className="ml-auto text-blue-600 font-semibold">{fmt(bcwp)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-400">÷</span>
                      <span className="text-blue-600 font-semibold">{fmt(acwp)}</span>
                      <span className="text-gray-400 ml-auto">=</span>
                      <span className={`font-extrabold text-base ${cpiColor(cpi)}`}>{cpi ? (cpi * 100).toFixed(1) + "%" : "—"}</span>
                    </div>
                  </div>
                )}
                {metric === "spi" && (() => {
                  const bcws = spi && bcwp ? bcwp / spi : null;
                  return (
                    <div className="space-y-3 text-sm">
                      <div className="flex items-center gap-2">
                        <span className="text-gray-400">≡</span>
                        <span className="text-gray-500">SPI = BCWP ÷ BCWS</span>
                        <span className="text-gray-400 mx-1">→</span>
                        <span className="ml-auto text-blue-600 font-semibold">{fmt(bcwp)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-gray-400">÷</span>
                        <span className="text-blue-600 font-semibold">{fmt(bcws)}</span>
                        <span className="text-gray-400 ml-auto">=</span>
                        <span className={`font-extrabold text-base ${cpiColor(spi)}`}>{spi ? (spi * 100).toFixed(1) + "%" : "—"}</span>
                      </div>
                    </div>
                  );
                })()}
                {metric === "eac" && (() => {
                  const etc = eac != null && acwp != null ? eac - acwp : null;
                  return (
                    <div className="space-y-3 text-sm">
                      <div className="flex items-center gap-2">
                        <span className="text-gray-400">≡</span>
                        <span className="text-gray-500">EAC = ACWP + ETC</span>
                        <span className="text-gray-400 mx-1">→</span>
                        <span className="ml-auto text-blue-600 font-semibold">{fmt(acwp)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-gray-400">+</span>
                        <span className="text-blue-600 font-semibold">{fmt(etc)}</span>
                        <span className="text-gray-400 ml-auto">=</span>
                        <span className="font-extrabold text-base text-gray-900">{fmt(eac)}</span>
                      </div>
                    </div>
                  );
                })()}
                {metric === "vac" && (
                  <div className="space-y-3 text-sm">
                    <div className="flex items-center gap-2">
                      <span className="text-gray-400">≡</span>
                      <span className="text-gray-500">VAC = BAC − EAC</span>
                      <span className="text-gray-400 mx-1">→</span>
                      <span className="ml-auto text-blue-600 font-semibold">{fmt(bac)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-400">−</span>
                      <span className="text-blue-600 font-semibold">{fmt(eac)}</span>
                      <span className="text-gray-400 ml-auto">=</span>
                      <span className={`font-extrabold text-base ${metrics.vac < 0 ? "text-red-600" : "text-green-600"}`}>{fmt(metrics.vac)}</span>
                    </div>
                  </div>
                )}
                {metric === "tcpi" && (() => {
                  const num = bac != null && bcwp != null ? bac - bcwp : null;
                  const den = eac != null && acwp != null ? eac - acwp : null;
                  return (
                    <div className="space-y-3 text-sm">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-gray-400">≡</span>
                        <span className="text-gray-500 text-xs">TCPI = (BAC−BCWP)/(EAC−ACWP)</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-blue-600 font-semibold">{fmt(num)}</span>
                        <span className="text-gray-400">÷</span>
                        <span className="text-blue-600 font-semibold">{fmt(den)}</span>
                        <span className="text-gray-400 ml-auto">=</span>
                        <span className="font-extrabold text-base text-gray-900">{tcpi_eac ? (tcpi_eac * 100).toFixed(1) + "%" : "—"}</span>
                      </div>
                    </div>
                  );
                })()}
                {metric === "cei" && (
                  <div className="space-y-3 text-sm">
                    <div className="flex items-center gap-2">
                      <span className="text-gray-400">≡</span>
                      <span className="text-gray-500">CEI = CPI × SPI</span>
                      <span className="text-gray-400 mx-1">→</span>
                      <span className="ml-auto text-blue-600 font-semibold">{cpi?.toFixed(3) ?? "—"}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-400">×</span>
                      <span className="text-blue-600 font-semibold">{spi?.toFixed(3) ?? "—"}</span>
                      <span className="text-gray-400 ml-auto">=</span>
                      <span className="font-extrabold text-base text-gray-900">{cei?.toFixed(3) ?? "—"}</span>
                    </div>
                  </div>
                )}
                {(metric === "bac" || metric === "mr") && (
                  <div className="text-sm space-y-2">
                    {metric === "bac" && <p className="text-gray-500">BAC <span className="text-gray-400 mx-1">→</span> <span className="font-bold text-gray-900">{fmt(bac)}</span></p>}
                    {metric === "mr" && <>
                      <p className="text-gray-500">MR Offset <span className="text-gray-400 mx-1">→</span> <span className="font-bold text-gray-900">{fmt(mr_offset)}</span></p>
                      <p className="text-gray-500">MR Remains <span className="text-gray-400 mx-1">→</span> <span className="font-bold text-gray-900">{fmt(mr_remains)}</span></p>
                    </>}
                  </div>
                )}
              </div>

              {/* Monthly Trend chart — flex-1 fills remaining gray box space */}
              {historyData.length > 1 && (
                <div className="flex-1 flex flex-col min-h-0">
                  <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">MONTHLY TREND</p>
                  <div className="flex-1 min-h-0">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={historyData} margin={{ top: 2, right: 2, left: 2, bottom: 2 }} barCategoryGap="30%">
                        <XAxis dataKey="label" tick={{ fontSize: 9, fill: "#9ca3af" }} tickLine={false} axisLine={false} />
                        <YAxis hide domain={[0, (dataMax: number) => Math.max(dataMax * 1.2, 1.0)]} />
                        <Tooltip contentStyle={{ fontSize: 10, padding: "2px 6px", borderRadius: 6 }} formatter={(v: any) => [(v * 100).toFixed(1) + "%", title]} />
                        <Bar dataKey="val" shape={<BarColorCell />} maxBarSize={36} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}
            </div>

          </div>

          {/* RIGHT TOP: Contributing factors */}
          <div>
            {factors.length > 0 && (
              <div>
                <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-2">CONTRIBUTING FACTORS</p>
                <div className="divide-y divide-gray-100">
                  {factors.map((f: string, i: number) => {
                    const pctMatch = f.match(/([+-]?\d+\.?\d*)\s*%\s*$/);
                    const pct = pctMatch ? pctMatch[1] : null;
                    const desc = pctMatch ? f.slice(0, f.lastIndexOf(pctMatch[0])).trim() : f;
                    const isNeg = pct ? parseFloat(pct) < 0 : true;
                    return (
                      <div key={i} className="flex items-start justify-between gap-3 py-2.5">
                        <div className="flex items-start gap-2.5">
                          <span className={`flex-shrink-0 w-5 h-5 rounded flex items-center justify-center mt-0.5 ${isNeg ? "bg-red-100" : "bg-green-100"}`}>
                            {isNeg ? (
                              <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="#b91c1c" strokeWidth="2.5" strokeLinecap="round">
                                <line x1="1.5" y1="1.5" x2="8.5" y2="8.5"/><line x1="8.5" y1="1.5" x2="1.5" y2="8.5"/>
                              </svg>
                            ) : (
                              <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="#15803d" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="1.5,5.5 4,8 8.5,2"/>
                              </svg>
                            )}
                          </span>
                          <span className="text-xs text-gray-700 leading-relaxed">{desc || f}</span>
                        </div>
                        {pct && (
                          <span className={`text-xs font-bold flex-shrink-0 ${isNeg ? "text-red-500" : "text-green-500"}`}>
                            {parseFloat(pct) > 0 ? "+" : ""}{pct}%
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* LEFT BOTTOM: Threshold box — only for CPI / SPI which have DB thresholds */}
          {hasThreshold && (
            <div className="h-full">
              <div className="bg-white border border-gray-200 rounded-xl p-4 h-full">
                <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-2">THRESHOLD FOR FLAGGING</p>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { label: "Blue",  range: `> ${(greenMax*100).toFixed(1)}%`,                                          color: "text-blue-600",  bg: "bg-blue-50  border-blue-200",  dot: "bg-blue-500"  },
                    { label: "Green", range: `${(greenMin*100).toFixed(1)} – ${(greenMax*100).toFixed(1)}%`,              color: "text-green-600", bg: "bg-green-50 border-green-200", dot: "bg-green-500" },
                    { label: "Amber", range: `${(yellowMin*100).toFixed(1)} – ${(greenMin*100).toFixed(1)}%`,             color: "text-amber-600", bg: "bg-amber-50 border-amber-200", dot: "bg-amber-400" },
                    { label: "Red",   range: `< ${(yellowMin*100).toFixed(1)}%`,                                          color: "text-red-600",   bg: "bg-red-50   border-red-200",   dot: "bg-red-500"   },
                  ].map(t => (
                    <div key={t.label} className={`rounded-xl border px-4 py-3 ${t.bg}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`w-2 h-2 rounded-full flex-shrink-0 ${t.dot}`} />
                        <span className={`text-sm font-semibold ${t.color}`}>{t.label}</span>
                      </div>
                      <p className={`text-sm ${t.color}`}>{t.range}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* RIGHT BOTTOM: Improvement to reach green — only for CPI / SPI */}
          {hasThreshold && currentVal != null && (
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-3">IMPROVEMENT TO REACH GREEN</p>
                <div className="divide-y divide-gray-100 mb-4">
                  <div className="flex justify-between items-center py-2">
                    <span className="text-xs text-gray-600">Current {title}</span>
                    <span className={`text-xs font-semibold ${cpiColor(currentVal)}`}>{(currentVal * 100).toFixed(1)}%</span>
                  </div>
                  <div className="flex justify-between items-center py-2">
                    <span className="text-xs text-gray-600">Green Threshold (Min)</span>
                    <span className="text-xs font-semibold text-green-600">{(greenMin * 100).toFixed(1)}%</span>
                  </div>
                </div>
                {alreadyGreen ? (
                  <div className="pt-1">
                    <p className="text-2xl font-bold text-green-600">Within Target</p>
                    <p className="text-xs text-gray-400 mt-1">{title} is already within the green threshold</p>
                  </div>
                ) : improvementNeeded && (
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Required Improvement:</p>
                    <p className={`text-3xl font-extrabold ${cpiColor(currentVal)}`}>+{improvementNeeded}%</p>
                    <p className="text-xs text-gray-400 mt-1">
                      {title} needs to improve by +{improvementNeeded}% to reach the Green threshold.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

        {/* AI Reasoning — full width, gray background box */}
        <div className="bg-gray-50 border border-gray-100 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <span className="w-6 h-6 bg-blue-500 rounded-lg flex items-center justify-center flex-shrink-0">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21h6"/><path d="M12 3a6 6 0 0 1 6 6c0 2.22-1.2 4.16-3 5.2V17a1 1 0 0 1-1 1H10a1 1 0 0 1-1-1v-2.8C7.2 13.16 6 11.22 6 9a6 6 0 0 1 6-6z"/>
              </svg>
            </span>
            <p className="text-xs font-bold text-blue-600 uppercase tracking-wider">AI REASONING</p>
          </div>
          {k ? (
            <ul className="text-xs text-gray-700 leading-relaxed space-y-1.5 list-disc list-inside">
              {k.headline && <li>{k.headline}</li>}
              {k.what_it_means && <li>{k.what_it_means}</li>}
              {k.trend && <li>{k.trend}</li>}
              {k.interpretation && <li>{k.interpretation}</li>}
              {k.buffer_analysis && <li>{k.buffer_analysis}</li>}
              {k.mr_context && <li>{k.mr_context}</li>}
              {k.worst_dept && <li>{k.worst_dept}</li>}
              {k.flag && <li className="text-red-600 font-semibold">{k.flag}</li>}
              {k.ieac_vs_eac && <li>{k.ieac_vs_eac}</li>}
              {k.required_action && (
                <li><span className="font-semibold text-gray-900">Required action: </span>{k.required_action}</li>
              )}
            </ul>
          ) : (
            <p className="text-xs text-gray-400">AI reasoning will appear after the next agent run.</p>
          )}
        </div>
      </div>
    );
  };

  const renderWpExpand = (d: any, colSpan: number) => {
    if (expandedDept !== d.obs) return null;
    const deptWps = (workPackages || [])
      .filter((wp: any) => wp.obs === d.obs)
      .sort((a: any, b: any) => (b.bac || 0) - (a.bac || 0));
    const totalBac = deptWps.reduce((s: number, wp: any) => s + (wp.bac || 0), 0);

    // Column headers + cell renderers matching each metric's dept-level columns
    type ColDef = { header: string; right?: boolean; render: (wp: any) => React.ReactNode };
    const colDefs: ColDef[] = (() => {
      const idCol:  ColDef = { header: "WP ID",        render: (wp) => <span className="font-mono text-blue-700 text-[11px] whitespace-nowrap">{wp.wp}</span> };
      const nameCol:ColDef = { header: "WORK PACKAGE", render: (wp) => <span className="text-gray-800 max-w-[200px] truncate block">{wp.description}</span> };
      const bacCol: ColDef = { header: "BAC",  right: true, render: (wp) => fmt(wp.bac) };
      const bcwpCol:ColDef = { header: "BCWP", right: true, render: (wp) => fmt(wp.pct_complete) };
      const acwpCol:ColDef = { header: "ACWP", right: true, render: (wp) => fmt(wp.acwp) };
      const cpiCol: ColDef = { header: "CPI%", right: true, render: (wp) => <span className={`font-bold ${cpiColor(wp.cpi)}`}>{wp.cpi ? (wp.cpi*100).toFixed(1)+"%" : "—"}</span> };
      const spiCol: ColDef = { header: "SPI%", right: true, render: (wp) => <span className={`font-bold ${cpiColor(wp.spi)}`}>{wp.spi ? (wp.spi*100).toFixed(1)+"%" : "—"}</span> };
      const eacCol: ColDef = { header: "EAC",  right: true, render: (wp) => <span className="font-medium">{fmt(wp.eac)}</span> };
      const overrunCol: ColDef = { header: "OVERRUN", right: true, render: (wp) => { const o=(wp.eac||0)-(wp.bac||0); return <span className={`font-medium ${o>0?"text-red-600":"text-green-600"}`}>{o>0?"+":""}{fmt(o)}</span>; }};
      const pctTotalCol: ColDef = { header: "% OF TOTAL", right: true, render: (wp) => { const t=totalBac; return t ? ((wp.bac/t)*100).toFixed(1)+"%" : "—"; }};
      const vacCol: ColDef = { header: "VAC", right: true, render: (wp) => <span className={`font-bold ${varColor(wp.vac)}`}>{fmt(wp.vac)}</span> };
      const ceiCol: ColDef = { header: "CEI (est.)", right: true, render: (wp) => { const c=wp.cpi&&wp.spi?wp.cpi*wp.spi:null; return <span className={`font-bold ${cpiColor(c)}`}>{c?(c*100).toFixed(1)+"%":"—"}</span>; }};
      const etcCol: ColDef = { header: "ETC REMAINING", right: true, render: (wp) => fmt(wp.etc) };
      const tcpiCol: ColDef = { header: "TCPI (est.)", right: true, render: (wp) => { const t=wp.etc&&wp.etc>0&&wp.bac?(wp.bac-(wp.pct_complete||0))/wp.etc:null; return <span className={`font-bold ${cpiColor(t)}`}>{t?(t*100).toFixed(1)+"%":"—"}</span>; }};
      const statusCol:ColDef = { header: "STATUS", render: (wp) => statusBadge(wp.cpi) };

      switch (metric) {
        case "cpi":  return [idCol, nameCol, bacCol, bcwpCol, acwpCol, cpiCol, statusCol];
        case "spi":  return [idCol, nameCol, bacCol, spiCol, statusCol];
        case "eac":  return [idCol, nameCol, bacCol, eacCol, overrunCol, cpiCol];
        case "bac":  return [idCol, nameCol, bacCol, pctTotalCol, cpiCol];
        case "vac":  return [idCol, nameCol, bacCol, eacCol, vacCol, cpiCol];
        case "cei":  return [idCol, nameCol, cpiCol, bacCol, ceiCol];
        case "tcpi": return [idCol, nameCol, etcCol, cpiCol, tcpiCol];
        default:     return [idCol, nameCol, bacCol, cpiCol, statusCol];
      }
    })();

    return (
      <tr key={`expand-${d.obs}`}>
        <td colSpan={colSpan} className="p-0 bg-blue-50/40 border-b border-blue-100">
          <div className="px-4 py-2 border-b border-blue-100 bg-blue-50/60">
            <span className="text-xs font-semibold text-gray-700">
              {d.obs} — {deptWps.length} Work Packages · sorted by BAC · {fmt(totalBac)} total BAC
            </span>
          </div>
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-gray-100/60">
                {colDefs.map(c => (
                  <th key={c.header} className={`px-3 py-2 font-medium text-gray-500 text-[10px] uppercase tracking-wide ${c.right ? "text-right" : "text-left"}`}>{c.header}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {deptWps.length === 0 ? (
                <tr><td colSpan={colDefs.length} className="px-3 py-4 text-center text-xs text-gray-400">No work packages found for this department.</td></tr>
              ) : deptWps.map((wp: any, i: number) => (
                <tr key={i} className="hover:bg-white/70">
                  {colDefs.map((c, ci) => (
                    <td key={ci} className={`px-3 py-2 ${c.right ? "text-right" : ""}`}>{c.render(wp)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </td>
      </tr>
    );
  };

  const renderBreakdown = () => {
    if (metric === "cpi") return (
      <table className="w-full text-xs">
        <thead className="bg-gray-50"><tr>{["Department","BAC","BCWP","ACWP","CPI%","Status",""].map(h=><th key={h} className="text-left px-3 py-2 font-medium text-gray-500">{h}</th>)}</tr></thead>
        <tbody className="divide-y divide-gray-100">
          {depts.map((d:any,i:number)=>(
            <React.Fragment key={i}>
              <tr className="hover:bg-blue-50/30 cursor-pointer" onClick={()=>setExpandedDept(expandedDept===d.obs?null:d.obs)}>
                <td className="px-3 py-2 font-medium text-gray-900">{d.obs} <span className="text-gray-400 font-normal text-xs">— {d.description}</span></td>
                <td className="px-3 py-2">{fmt(d.bac)}</td>
                <td className="px-3 py-2">{fmt(d.bcwp)}</td>
                <td className="px-3 py-2">{fmt(d.acwp)}</td>
                <td className={`px-3 py-2 font-bold ${cpiColor(d.cpi)}`}>{d.cpi?(d.cpi*100).toFixed(1)+"%":"—"}</td>
                <td className="px-3 py-2">{statusBadge(d.cpi)}</td>
                <td className="px-3 py-2 text-gray-400 text-xs">{expandedDept===d.obs?"▼":"▶"}</td>
              </tr>
              {renderWpExpand(d, 7)}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    );
    if (metric === "spi") return (
      <table className="w-full text-xs">
        <thead className="bg-gray-50"><tr>{["Department","BAC","SPI%","Status",""].map(h=><th key={h} className="text-left px-3 py-2 font-medium text-gray-500">{h}</th>)}</tr></thead>
        <tbody className="divide-y divide-gray-100">
          {depts.map((d:any,i:number)=>{
            const spi=d.bac&&d.eac?Math.min(d.bac/d.eac,1.2):null;
            return(
              <React.Fragment key={i}>
                <tr className="hover:bg-blue-50/30 cursor-pointer" onClick={()=>setExpandedDept(expandedDept===d.obs?null:d.obs)}>
                  <td className="px-3 py-2 font-medium text-gray-900">{d.obs} <span className="text-gray-400 font-normal">— {d.description}</span></td>
                  <td className="px-3 py-2">{fmt(d.bac)}</td>
                  <td className={`px-3 py-2 font-bold ${cpiColor(spi)}`}>{spi?(spi*100).toFixed(1)+"%":"—"}</td>
                  <td className="px-3 py-2">{statusBadge(spi)}</td>
                  <td className="px-3 py-2 text-gray-400 text-xs">{expandedDept===d.obs?"▼":"▶"}</td>
                </tr>
                {renderWpExpand(d, 5)}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    );
    if (metric === "eac") return (
      <table className="w-full text-xs">
        <thead className="bg-gray-50"><tr>{["Department","BAC","EAC","Overrun","CPI",""].map(h=><th key={h} className={`px-3 py-2 font-medium text-gray-500 ${h!=="Department"&&h!==""?"text-right":"text-left"}`}>{h}</th>)}</tr></thead>
        <tbody className="divide-y divide-gray-100">
          {depts.map((d:any,i:number)=>{const overrun=d.eac-d.bac;return(
            <React.Fragment key={i}>
              <tr className="hover:bg-blue-50/30 cursor-pointer" onClick={()=>setExpandedDept(expandedDept===d.obs?null:d.obs)}>
                <td className="px-3 py-2 font-medium text-gray-900">{d.obs}</td>
                <td className="px-3 py-2 text-right">{fmt(d.bac)}</td><td className="px-3 py-2 text-right font-medium">{fmt(d.eac)}</td>
                <td className={`px-3 py-2 text-right font-medium ${overrun>0?"text-red-600":"text-green-600"}`}>{overrun>0?"+":""}{fmt(overrun)}</td>
                <td className={`px-3 py-2 text-right font-bold ${cpiColor(d.cpi)}`}>{d.cpi?(d.cpi*100).toFixed(1)+"%":"—"}</td>
                <td className="px-3 py-2 text-gray-400 text-xs">{expandedDept===d.obs?"▼":"▶"}</td>
              </tr>
              {renderWpExpand(d, 6)}
            </React.Fragment>
          );})}
        </tbody>
      </table>
    );
    if (metric === "bac") return (
      <table className="w-full text-xs">
        <thead className="bg-gray-50"><tr>{["Department","BAC","% of Total","CPI",""].map(h=><th key={h} className={`px-3 py-2 font-medium text-gray-500 ${h!=="Department"&&h!==""?"text-right":"text-left"}`}>{h}</th>)}</tr></thead>
        <tbody className="divide-y divide-gray-100">
          {depts.map((d:any,i:number)=>{
            const total=depts.reduce((s:number,x:any)=>s+(x.bac||0),0);
            const pct=total?((d.bac/total)*100).toFixed(1):"—";
            return(
              <React.Fragment key={i}>
                <tr className="hover:bg-blue-50/30 cursor-pointer" onClick={()=>setExpandedDept(expandedDept===d.obs?null:d.obs)}>
                  <td className="px-3 py-2 font-medium text-gray-900">{d.obs}</td>
                  <td className="px-3 py-2 text-right">{fmt(d.bac)}</td>
                  <td className="px-3 py-2 text-right">{pct}%</td>
                  <td className={`px-3 py-2 text-right font-bold ${cpiColor(d.cpi)}`}>{d.cpi?(d.cpi*100).toFixed(1)+"%":"—"}</td>
                  <td className="px-3 py-2 text-gray-400 text-xs">{expandedDept===d.obs?"▼":"▶"}</td>
                </tr>
                {renderWpExpand(d, 5)}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    );
    if (metric === "vac") return (
      <table className="w-full text-xs">
        <thead className="bg-gray-50"><tr>{["Department","BAC","EAC","VAC","CPI",""].map(h=><th key={h} className={`px-3 py-2 font-medium text-gray-500 ${h!=="Department"&&h!==""?"text-right":"text-left"}`}>{h}</th>)}</tr></thead>
        <tbody className="divide-y divide-gray-100">
          {depts.map((d:any,i:number)=>(
            <React.Fragment key={i}>
              <tr className="hover:bg-blue-50/30 cursor-pointer" onClick={()=>setExpandedDept(expandedDept===d.obs?null:d.obs)}>
                <td className="px-3 py-2 font-medium text-gray-900">{d.obs}</td>
                <td className="px-3 py-2 text-right">{fmt(d.bac)}</td><td className="px-3 py-2 text-right">{fmt(d.eac)}</td>
                <td className={`px-3 py-2 text-right font-bold ${varColor(d.vac)}`}>{fmt(d.vac)}</td>
                <td className={`px-3 py-2 text-right font-bold ${cpiColor(d.cpi)}`}>{d.cpi?(d.cpi*100).toFixed(1)+"%":"—"}</td>
                <td className="px-3 py-2 text-gray-400 text-xs">{expandedDept===d.obs?"▼":"▶"}</td>
              </tr>
              {renderWpExpand(d, 6)}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    );
    if (metric === "mr") {
      const etc=depts.reduce((s:number,d:any)=>s+(d.etc||0),0);
      const mrR=metrics?.mr_remains||0;const mrO=metrics?.mr_offset||0;
      const mrOrig=mrR+Math.abs(mrO);
      const mrPct=etc>0?((mrR/etc)*100).toFixed(1):"—";
      return(
        <table className="w-full text-xs">
          <thead className="bg-gray-50"><tr>{["Item","Amount"].map(h=><th key={h} className={`px-3 py-2 font-medium text-gray-500 ${h==="Amount"?"text-right":"text-left"}`}>{h}</th>)}</tr></thead>
          <tbody className="divide-y divide-gray-100">
            {[["MR Original",fmt(mrOrig),"text-gray-900"],["MR Drawdown (this period)",fmt(-Math.abs(mrO)),"text-red-600"],["MR Remaining",fmt(mrR),"text-green-600"],[`MR % Remaining (vs ETC)`,`${mrPct}%`,"text-gray-900"]].map(([l,v,c])=>(
              <tr key={l} className="hover:bg-gray-50"><td className="px-3 py-2 font-medium text-gray-900">{l}</td><td className={`px-3 py-2 text-right font-bold ${c}`}>{v}</td></tr>
            ))}
          </tbody>
        </table>
      );
    }
    if (metric === "cei") return (
      <table className="w-full text-xs">
        <thead className="bg-gray-50"><tr>{["Department","CPI","BAC","CEI (est.)",""].map(h=><th key={h} className={`px-3 py-2 font-medium text-gray-500 ${h!=="Department"&&h!==""?"text-right":"text-left"}`}>{h}</th>)}</tr></thead>
        <tbody className="divide-y divide-gray-100">
          {depts.map((d:any,i:number)=>{
            const spi=d.bac&&d.eac?Math.min(d.bac/d.eac,1.2):1;
            const cei=d.cpi&&spi?d.cpi*spi:null;
            return(
              <React.Fragment key={i}>
                <tr className="hover:bg-blue-50/30 cursor-pointer" onClick={()=>setExpandedDept(expandedDept===d.obs?null:d.obs)}>
                  <td className="px-3 py-2 font-medium text-gray-900">{d.obs}</td>
                  <td className={`px-3 py-2 text-right font-bold ${cpiColor(d.cpi)}`}>{d.cpi?(d.cpi*100).toFixed(1)+"%":"—"}</td>
                  <td className="px-3 py-2 text-right">{fmt(d.bac)}</td>
                  <td className={`px-3 py-2 text-right font-bold ${cpiColor(cei)}`}>{cei?(cei*100).toFixed(1)+"%":"—"}</td>
                  <td className="px-3 py-2 text-gray-400 text-xs">{expandedDept===d.obs?"▼":"▶"}</td>
                </tr>
                {renderWpExpand(d, 5)}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    );
    if (metric === "tcpi") return (
      <table className="w-full text-xs">
        <thead className="bg-gray-50"><tr>{["Department","ETC Remaining","CPI","TCPI (est.)",""].map(h=><th key={h} className={`px-3 py-2 font-medium text-gray-500 ${h!=="Department"&&h!==""?"text-right":"text-left"}`}>{h}</th>)}</tr></thead>
        <tbody className="divide-y divide-gray-100">
          {depts.map((d:any,i:number)=>{
            const tcpi=d.etc&&d.etc>0&&d.bac?(d.bac-(d.bcwp||0))/d.etc:null;
            return(
              <React.Fragment key={i}>
                <tr className="hover:bg-blue-50/30 cursor-pointer" onClick={()=>setExpandedDept(expandedDept===d.obs?null:d.obs)}>
                  <td className="px-3 py-2 font-medium text-gray-900">{d.obs}</td>
                  <td className="px-3 py-2 text-right">{fmt(d.etc)}</td>
                  <td className={`px-3 py-2 text-right font-bold ${cpiColor(d.cpi)}`}>{d.cpi?(d.cpi*100).toFixed(1)+"%":"—"}</td>
                  <td className={`px-3 py-2 text-right font-bold ${cpiColor(tcpi)}`}>{tcpi?(tcpi*100).toFixed(1)+"%":"—"}</td>
                  <td className="px-3 py-2 text-gray-400 text-xs">{expandedDept===d.obs?"▼":"▶"}</td>
                </tr>
                {renderWpExpand(d, 5)}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    );
    return <p className="text-sm text-gray-400 p-4">No data available.</p>;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col" onClick={e=>e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <p className="text-lg font-bold text-gray-900">{title}</p>
            <p className="text-xs text-gray-400">{subtitle}</p>
          </div>
          <div className="flex items-center gap-2">
            {metric !== "mr" && (
              <>
                <button onClick={()=>setModalTab("reasoning")} className={`px-3 py-1.5 rounded-lg text-xs font-medium border ${modalTab==="reasoning"?"bg-blue-600 text-white border-blue-600":"bg-white text-gray-600 border-gray-200"}`}>
                  {title} REASONING
                </button>
                <button onClick={()=>setModalTab("breakdown")} className={`px-3 py-1.5 rounded-lg text-xs font-medium border ${modalTab==="breakdown"?"bg-blue-600 text-white border-blue-600":"bg-white text-gray-600 border-gray-200"}`}>
                  DEPT BREAKDOWN
                </button>
              </>
            )}
            <button onClick={onClose} className="ml-2 w-7 h-7 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-400">✕</button>
          </div>
        </div>
        <div className="overflow-y-auto flex-1 p-6">
          {modalTab === "reasoning" ? renderReasoning() : renderBreakdown()}
        </div>
      </div>
    </div>
  );
}

const VarianceRow = ({ v, i }: any) => {
  const [expanded, setExpanded] = React.useState(false);
  return (
    <>
      <tr className="hover:bg-gray-50 cursor-pointer border-b border-gray-100" onClick={()=>setExpanded(!expanded)}>
        <td className="px-4 py-3 text-xs text-gray-400">{i+1}</td>
        <td className="px-4 py-3"><span className="text-xs font-mono bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded">{v.obs}</span></td>
        <td className="px-4 py-3 text-xs text-gray-900 font-medium max-w-xs truncate">{v.description}</td>
        <td className={`px-4 py-3 text-xs font-bold ${v.dollar_amount<0?"text-red-600":"text-green-600"}`}>{v.dollar_amount>0?"+":""}{fmt(v.dollar_amount)}</td>
        <td className={`px-4 py-3 text-xs font-bold ${cpiColor(v.cum_cpi)}`}>{v.cum_cpi?.toFixed(3)||"—"}</td>
        <td className={`px-4 py-3 text-xs font-medium ${varColor(v.vac)}`}>{fmt(v.vac)}</td>
        <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded-full font-medium border ${v.direction==="unfavorable"?"bg-red-50 text-red-600 border-red-200":"bg-green-50 text-green-600 border-green-200"}`}>{v.direction}</span></td>
        <td className="px-4 py-3 text-gray-400 text-xs">{expanded?"▲":"▼"}</td>
      </tr>
      {expanded&&(
        <tr className="bg-blue-50 border-b border-blue-100">
          <td colSpan={8} className="px-6 py-4">
            {v.explanation&&(
              <div className="mb-4 p-3 bg-white rounded-lg border border-blue-100">
                <p className="text-xs font-semibold text-blue-700 mb-1">AI Analysis</p>
                <p className="text-xs text-gray-700 leading-relaxed">{v.explanation}</p>
              </div>
            )}
            <div className="grid grid-cols-4 gap-4 text-xs">
              {[["BAC",fmt(v.bac)],["EAC",fmt(v.eac)],["Monthly EAC Δ",fmt(v.evidence?.bw_month_eac)],["Quarterly EAC Δ",fmt(v.evidence?.bw_qtrly_eac)],
                ["Forecast Finish",v.forecast_finish?.split("T")[0]||"—"],["Baseline Finish",v.baseline_finish?.split("T")[0]||"—"],["CAM",v.cam||"—"],["WBS",v.wbs||"—"]
              ].map(([l,val])=>(<div key={l}><p className="text-gray-400 mb-1">{l}</p><p className="font-semibold text-gray-900">{val}</p></div>))}
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

export default function ReportDetail() {
  const { reportId } = useParams();
  const [report, setReport] = useState<any>(null);
  const [project, setProject] = useState<any>(null);
  const [performance, setPerformance] = useState<any>(null);
  const [schedule, setSchedule] = useState<any>(null);
  const [staffing, setStaffing] = useState<any>(null);
  const [financials, setFinancials] = useState<any>(null);
  const [workPackages, setWorkPackages] = useState<any[]>([]);
  const [wpSearch, setWpSearch] = useState("");
  const [wpPage, setWpPage] = useState(1);
  const WP_PAGE_SIZE = 50;
  const [tab, setTab] = useState("summary");
  const [jiraRisks, setJiraRisks] = useState<any[]>([]);
  const [jiraAvailable, setJiraAvailable] = useState(false);
  const [modal, setModal] = useState<string | null>(null);
  const [isEditingSummary, setIsEditingSummary] = useState(false);
  const [editedSummaryText, setEditedSummaryText] = useState("");
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [auditTrail, setAuditTrail] = useState<any[]>([]);
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditLoading, setAuditLoading] = useState(false);

  // Customer feedback
  const [custFeedbacks, setCustFeedbacks] = useState<any[]>([]);
  const [custSelected, setCustSelected] = useState<Set<string>>(new Set());
  const [custModal, setCustModal] = useState<"add" | "edit" | null>(null);
  const [custEditing, setCustEditing] = useState<any>(null);
  const [custForm, setCustForm] = useState({ name: "", role: "", feedback: "", sentiment: "NEUTRAL", used_in_ai: false, flagged: false });
  const [imsMilestones, setImsMilestones] = useState<any[]>([]);
  const projectId = localStorage.getItem("active_project_id") || "";

  useEffect(() => {
    api.get(`/api/reports/${reportId}`).then(r=>{
      setReport(r.data);
      const pid = r.data?.project_id || projectId;
      if(pid) api.get(`/api/projects/${pid}`).then(p=>setProject(p.data)).catch(()=>{});
    });
    api.get(`/api/reports/${reportId}/performance`).then(r=>setPerformance(r.data)).catch(()=>{});
    api.get(`/api/reports/${reportId}/schedule`).then(r=>setSchedule(r.data)).catch(()=>{});
    api.get(`/api/reports/${reportId}/staffing`).then(r=>setStaffing(r.data)).catch(()=>{});
    api.get(`/api/reports/${reportId}/financials`).then(r=>setFinancials(r.data)).catch(()=>{});
    api.get(`/api/reports/${reportId}/workpackages`).then(r=>setWorkPackages(r.data?.work_packages||[])).catch(()=>{});
    api.get(`/api/data/jira/risks?report_id=${reportId}`).then(r=>{setJiraAvailable(r.data.available||false);setJiraRisks(r.data.jira_risks||[]);}).catch(()=>{});
    api.get(`/api/customer-feedback/${reportId}`).then(r=>setCustFeedbacks(r.data)).catch(()=>{});
    api.get(`/api/data/ims/milestones?report_id=${reportId}`).then(r=>setImsMilestones(r.data.upcoming||[])).catch(()=>{});
  }, [reportId]);

  if (!report) return <div className="min-h-screen flex items-center justify-center bg-gray-50"><p className="text-gray-500 text-sm">Loading report...</p></div>;

  const out = report.output || {};
  const exec = out.executive_summary || {};
  const metrics = exec.metrics || {};
  const variances = out.variances?.variance_list || [];
  const summaryBullets: any[] = exec.summary_bullets || [];
  const kpiAnalysis: any = out.kpi_analysis || {};
  const achievements = exec.achievements || [];
  const upcomingMilestones = exec.upcoming_milestones || [];
  const feedbacks = report.config?.customer_feedback || [];
  const pctComplete = metrics.pct_complete != null ? (metrics.pct_complete * 100).toFixed(1) : null;
  const priorMetrics = exec.prior_metrics || {};
  const vacDelta = priorMetrics.vac != null && metrics.vac != null ? metrics.vac - priorMetrics.vac : null;
  const spiDelta = priorMetrics.spi != null && metrics.spi != null ? metrics.spi - priorMetrics.spi : null;

  const modalConfigs: Record<string, {title:string;subtitle:string}> = {
    cpi:{title:"CPI",subtitle:"Cost Performance by Dept"},
    spi:{title:"SPI",subtitle:"Schedule Performance by Dept"},
    cei:{title:"CEI",subtitle:"Current Execution Index (CPI × SPI)"},
    tcpi:{title:"TCPIeac",subtitle:"To-Complete Performance Index relative to EAC"},
    eac:{title:"EAC",subtitle:"EAC vs BAC by Department"},
    bac:{title:"BAC",subtitle:"Budget at Completion"},
    vac:{title:"VAC",subtitle:"Variance at Completion"},
    mr:{title:"MR",subtitle:"Management Reserve Summary"},
  };

  const tabs = [
    {id:"summary",label:"Executive"},{id:"performance",label:"Performance"},
    {id:"financials",label:"Financials"},{id:"schedule",label:"Schedule"},
    {id:"staffing",label:"Staffing"},{id:"risks",label:"Risks"},
    {id:"variances",label:"Variances"},{id:"milestones",label:"Milestones"},

  ];

  const ViewMore = ({k}:{k:string}) => (
    <button onClick={()=>setModal(k)} className="text-xs text-blue-500 hover:text-blue-700 flex items-center gap-0.5 whitespace-nowrap">
      VIEW MORE <span>→</span>
    </button>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {modal && modalConfigs[modal] && (
        <MetricModal metric={modal} title={modalConfigs[modal].title} subtitle={modalConfigs[modal].subtitle}
          onClose={()=>setModal(null)} financials={financials} metrics={metrics} kpiAnalysis={kpiAnalysis} reportId={reportId} workPackages={workPackages}/>
      )}

      <ProjectNav projectId={projectId} active="history" />

      <div className="w-full px-6 py-5">
        <div className="flex gap-0 border-b border-gray-200 mb-5 overflow-x-auto">
          {tabs.map(t=>(
            <button key={t.id} onClick={()=>setTab(t.id)}
              className={`px-5 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${tab===t.id?"border-blue-600 text-blue-600":"border-transparent text-gray-500 hover:text-gray-700"}`}>
              {t.label}
            </button>
          ))}
        </div>

        {/* EXECUTIVE */}
        {tab==="summary"&&(
          <div className="space-y-4">
            <div className="flex gap-4">
              {/* Program Info */}
              <div className="w-[435px] flex-shrink-0 bg-white rounded-xl border border-gray-200 p-5">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">PROGRAM INFO</p>
                <div className="space-y-2.5 divide-y divide-gray-100">
                  {[["Project Name",project?.name||"M170 BP884"],["Aircraft Platform",project?.aircraft_platform||"BOMBARDIER GLOBAL 7500 AVIONICS"],["Month",report.version_label?.replace(/-v\d+$/,"")||report.config?.hitl_answers?.quarter||report.quarter||"—"],["Data as of",report.data_as_of||report.config?.data_as_of||"—"]].map(([l,v])=>(
                    <div key={l} className="flex justify-between text-xs gap-3"><span className="text-gray-400 flex-shrink-0">{l}</span><span className="font-medium text-gray-900 text-right">{v}</span></div>
                  ))}
                </div>

              </div>

              {/* Key Metrics */}
              <div className="flex-1 min-w-0 bg-white rounded-xl border border-gray-200 p-5">
                <div className="flex items-center justify-between mb-4">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">KEY METRICS</p>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-gray-400">% Complete (EARNED TO DATE)</span>
                    <span className="text-xs font-bold text-gray-900">{pctComplete?`${pctComplete}%`:"—"}</span>
                    <div className="w-24 bg-gray-100 rounded-full h-1.5">
                      <div className="h-1.5 bg-green-500 rounded-full" style={{width:`${pctComplete||0}%`}}/>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-5 divide-x divide-gray-100 mb-4">
                  {[
                    {k:"cpi",label:"CPI",value:metrics.cpi?(metrics.cpi*100).toFixed(1)+"%":"—",raw:metrics.cpi,colored:true,sub:"Cost Performance"},
                    {k:"spi",label:"SPI",value:metrics.spi?(metrics.spi*100).toFixed(1)+"%":"—",raw:metrics.spi,colored:true,sub:"Schedule Performance",delta:spiDelta!=null?`${spiDelta>0?"+":""}${(spiDelta*100).toFixed(2)}%`:null,deltaUp:spiDelta!=null?spiDelta>0:null},
                    {k:"cei",label:"CEI",value:metrics.cei?metrics.cei.toFixed(2):"—",raw:metrics.cei,colored:false,sub:"Current Execution Index"},
                    {k:"tcpi",label:"TCPI",value:metrics.tcpi_eac?(metrics.tcpi_eac*100).toFixed(1)+"%":"—",raw:metrics.tcpi_eac,colored:false,sub:"To-Complete Performance Index"},
                    {k:"eac",label:"EAC",value:fmt(metrics.eac),raw:null,colored:false,sub:"Estimate at Completion"},
                  ].map(m=>(
                    <div key={m.k} className="px-4 py-1">
                      <div className="flex items-center justify-between mb-2">
                        <span className="flex items-center gap-1.5 text-xs font-medium text-gray-500">
                          {m.label}
                          {m.colored&&m.raw!=null&&<span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${cpiDot(m.raw)}`}></span>}
                        </span>
                        <ViewMore k={m.k}/>
                      </div>
                      <div className="flex items-center justify-between gap-2 mb-0.5">
                        <p className={`text-3xl font-bold ${m.colored&&m.raw!=null?cpiColor(m.raw):"text-gray-900"}`}>{m.value}</p>
                        {m.delta&&(
                          <span className={`flex-shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${m.deltaUp?"bg-green-50 text-green-600":"bg-red-50 text-red-600"}`}>
                            {m.deltaUp?"↑":"↓"} {m.delta}
                          </span>
                        )}
                      </div>
                      {m.delta&&<p className="text-[10px] text-gray-400 text-right">vs prior</p>}
                      <p className="text-xs text-gray-400 leading-tight mt-0.5">{m.sub}</p>
                    </div>
                  ))}
                </div>
                <div className="border-t border-gray-100 mb-4"></div>
                <div className="grid grid-cols-4 divide-x divide-gray-100">
                  {[
                    {k:"bac",label:"BAC",value:fmt(metrics.bac),color:"text-gray-900",dot:false,sub:"Budget at Completion"},
                    {k:"vac",label:"VAC",value:fmt(metrics.vac),color:varColor(metrics.vac),dot:true,sub:"Variance at Completion",delta:vacDelta!=null?`${vacDelta>0?"+":""}${fmt(vacDelta)}`:null,deltaUp:vacDelta!=null?vacDelta>0:null},
                    {k:"mr",label:"MR OFFSET",value:fmt(metrics.mr_offset),color:"text-gray-900",dot:false,sub:"MR applied to offset variances"},
                    {k:"mr",label:"MR REMAINS",value:fmt(metrics.mr_remains),color:"text-gray-900",dot:false,sub:"Management Reserve remaining"},
                  ].map((m,i)=>(
                    <div key={i} className="px-4 py-1">
                      <div className="flex items-center justify-between mb-2">
                        <span className="flex items-center gap-1.5 text-xs font-medium text-gray-500">
                          {m.label}
                          {m.dot&&<span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${metrics.vac<0?"bg-red-500":"bg-green-500"}`}></span>}
                        </span>
                        <ViewMore k={m.k}/>
                      </div>
                      <div className="flex items-center justify-between gap-2">
                        <p className={`text-2xl font-bold ${m.color}`}>{m.value||"—"}</p>
                        {m.delta&&(
                          <span className={`flex-shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${metrics.vac<0?"bg-red-50 text-red-600":"bg-green-50 text-green-600"}`}>
                            {m.deltaUp?"↑":"↓"} {m.delta}
                          </span>
                        )}
                      </div>
                      {m.delta&&<p className="text-[10px] text-gray-400 text-right">vs prior</p>}
                      <p className="text-xs text-gray-400 mt-0.5 leading-tight">{m.sub}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Summary — full width */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-semibold text-gray-900">SUMMARY</p>
                <div className="flex gap-2">
                  <button
                    onClick={async () => {
                      setAuditOpen(true);
                      setAuditLoading(true);
                      try {
                        const r = await api.get(`/api/audit/${reportId}`);
                        setAuditTrail(r.data);
                      } catch { setAuditTrail([]); }
                      finally { setAuditLoading(false); }
                    }}
                    className="text-xs text-blue-600 border border-blue-200 rounded-full px-2.5 py-1 hover:bg-blue-50">
                    🔍 Audit Trail
                  </button>
                  {!isEditingSummary ? (
                    <button
                      onClick={() => {
                        setEditedSummaryText(summaryBullets.join("\n"));
                        setIsEditingSummary(true);
                      }}
                      className="text-xs text-blue-600 border border-blue-200 rounded-full px-2.5 py-1 hover:bg-blue-50">
                      ✏ Edit Summary
                    </button>
                  ) : (
                    <div className="flex gap-1.5">
                      <button
                        onClick={() => setIsEditingSummary(false)}
                        className="text-xs text-gray-500 border border-gray-200 rounded-full px-2.5 py-1 hover:bg-gray-50">
                        Cancel
                      </button>
                      <button
                        disabled={summaryLoading}
                        onClick={async () => {
                          const bullets = editedSummaryText
                            .split("\n")
                            .map(l => l.replace(/^[\s•\-*]+/, "").trim())
                            .filter(Boolean);
                          setSummaryLoading(true);
                          try {
                            const before = summaryBullets;
                            await api.patch(`/api/reports/${reportId}/summary`, { summary_bullets: bullets });
                            await api.post("/api/audit", {
                              report_id:  reportId,
                              action:     "edit_summary",
                              field:      "summary_bullets",
                              before_val: before,
                              after_val:  bullets,
                            });
                            setReport((prev: any) => {
                              const updated = { ...prev };
                              if (!updated.output) updated.output = {};
                              if (!updated.output.executive_summary) updated.output.executive_summary = {};
                              updated.output.executive_summary.summary_bullets = bullets;
                              return updated;
                            });
                            setIsEditingSummary(false);
                          } catch { /* keep editing open on error */ }
                          finally { setSummaryLoading(false); }
                        }}
                        className="text-xs bg-blue-600 text-white rounded-full px-2.5 py-1 hover:bg-blue-700 disabled:opacity-50">
                        {summaryLoading ? "Saving…" : "Save"}
                      </button>
                    </div>
                  )}
                </div>
              </div>
              {isEditingSummary ? (
                <textarea
                  autoFocus
                  value={editedSummaryText}
                  onChange={e => setEditedSummaryText(e.target.value)}
                  rows={Math.max(6, editedSummaryText.split("\n").length + 1)}
                  placeholder="One bullet per line. Leading • or - are stripped automatically."
                  className="w-full text-xs text-gray-700 border border-blue-200 rounded-lg p-3 resize-y focus:outline-none focus:ring-2 focus:ring-blue-300 font-mono leading-relaxed"
                />
              ) : summaryBullets.length > 0 ? (
                <ul className="space-y-2.5">{summaryBullets.map((b:string,i:number)=>(
                  <li key={i} className="flex items-start gap-2 text-xs text-gray-700">
                    <span className="text-gray-400 mt-0.5 flex-shrink-0">•</span>
                    <span>{b}</span>
                  </li>
                ))}</ul>
              ) : <p className="text-xs text-gray-400">AI summary will appear after report generation.</p>}
            </div>

            {/* Key Variances (left) + Recent Achievements & Upcoming Milestones (right) */}
            <div className="grid grid-cols-2 gap-4">
              {/* Left: Key Variances */}
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-semibold text-gray-900">KEY VARIANCES VS PRIOR QUARTER</p>
                  <button onClick={()=>setTab("variances")} className="text-xs text-blue-600 hover:underline flex items-center gap-1">View All →</button>
                </div>
                <div className="divide-y divide-gray-100">
                  {variances.slice(0,4).map((v:any,i:number)=>(
                    <div key={i} className="flex items-start justify-between gap-3 py-3 first:pt-0 hover:bg-gray-50 cursor-pointer" onClick={()=>setTab("variances")}>
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        <span className="text-xs text-gray-400 w-4 flex-shrink-0 mt-0.5">{i+1}</span>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-gray-900">{v.description}</p>
                          <p className="text-xs text-gray-400 uppercase tracking-wide mt-0.5">{v.obs} — {v.cam}</p>
                          {v.explanation&&<p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{v.explanation}</p>}
                        </div>
                      </div>
                      <span className={`text-xs font-bold flex-shrink-0 whitespace-nowrap mt-0.5 ${v.dollar_amount<0?"text-red-600":"text-green-600"}`}>{v.dollar_amount>0?"+":""}{fmt(v.dollar_amount)} ›</span>
                    </div>
                  ))}
                  {variances.length===0&&<p className="text-xs text-gray-400">No significant variances detected.</p>}
                </div>
              </div>

              {/* Right: Recent Achievements + Upcoming Milestones stacked */}
              <div className="space-y-4">
                <div className="bg-white rounded-xl border border-gray-200 p-5">
                  <p className="text-sm font-semibold text-gray-900 mb-3">RECENT ACHIEVEMENTS</p>
                  {achievements.length>0?(
                    <div className="space-y-2">{achievements.map((a:any,i:number)=>(
                      <div key={i} className="flex items-start gap-2">
                        <svg className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7"/></svg>
                        <p className="text-xs text-gray-700">{a.description}</p>
                      </div>
                    ))}</div>
                  ):<p className="text-xs text-gray-400">No recently completed work packages.</p>}
                </div>
                <div className="bg-white rounded-xl border border-gray-200 p-5">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-sm font-semibold text-gray-900">UPCOMING MILESTONES</p>
                    <button onClick={()=>setTab("schedule")} className="text-xs text-blue-600 hover:underline">Schedule</button>
                  </div>
                  {imsMilestones.length>0?(
                    <div className="space-y-2.5">
                      {imsMilestones.slice(0,3).map((m:any,i:number)=>(
                        <div key={i} className="flex items-center gap-3 bg-gray-50 rounded-xl p-3">
                          <div className="w-9 h-9 rounded-lg bg-white shadow-sm flex items-center justify-center flex-shrink-0">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                              <line x1="16" y1="2" x2="16" y2="6"/>
                              <line x1="8" y1="2" x2="8" y2="6"/>
                              <line x1="3" y1="10" x2="21" y2="10"/>
                            </svg>
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-semibold text-gray-800 truncate">{m.name}</p>
                            <p className="text-xs text-blue-500 font-medium uppercase tracking-wide">{m.finish_label||m.finish_date||"—"}</p>
                          </div>
                          <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${m.status==="on_track"||m.status==="complete"?"bg-green-500":"bg-amber-500"}`}/>
                        </div>
                      ))}
                    </div>
                  ):(
                    <div className="flex flex-col items-center justify-center py-8 text-center">
                      <span className="text-3xl mb-2">📋</span>
                      <p className="font-medium text-gray-700">IMS Integration Pending</p>
                      <p className="text-sm text-gray-400 text-center mt-1">Upload an IMS file during report processing to see milestones.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Key Risks */}
            {jiraAvailable&&jiraRisks.length>0&&(
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-semibold text-gray-900">KEY RISKS</p>
                  <button onClick={()=>setTab("risks")} className="text-xs text-blue-600 hover:underline">View All →</button>
                </div>
                <table className="w-full text-xs">
                  <thead className="bg-gray-50"><tr><th className="w-1 p-0"></th>{["Risk","Impact Level","Probability","Exposure","Trend","Mitigation"].map(h=><th key={h} className={`px-3 py-2 font-medium text-gray-500 ${h==="Risk"?"text-left":"text-center"}`}>{h}</th>)}</tr></thead>
                  <tbody className="divide-y divide-gray-50">
                    {jiraRisks.slice(0,8).map((r:any,i:number)=>{
                      const rawLevel = r.factored_cost_k >= 1000 ? "Severe" : r.factored_cost_k >= 300 ? "Significant" : "Moderate";
                      const level = rawLevel;
                      const barColor=!r.impact_index?"bg-transparent":(r.impact_index.includes("Severe")?"bg-red-500":r.impact_index.includes("Significant")?"bg-orange-400":r.impact_index.includes("Minor")?"bg-green-500":"bg-yellow-400");
                      const mitigationCls:Record<string,string>={
                        "Under Review":"bg-orange-50 text-orange-700 border-orange-200",
                        "RCA In Progress":"bg-red-50 text-red-700 border-red-200",
                        "Monitoring":"bg-blue-50 text-blue-700 border-blue-200",
                        "Mitigated":"bg-green-50 text-green-700 border-green-200",
                        "On Track":"bg-green-50 text-green-600 border-green-200",
                      };
                      return (
                      <tr key={i} className="hover:bg-gray-50">
                        <td className={`w-1 p-0 ${barColor}`}></td>
                        <td className="py-3 px-2">
                          <div className="flex items-center gap-2">
                            <button className="text-gray-400 text-xs flex-shrink-0">›</button>
                            <div>
                              <p className="font-bold text-gray-900 truncate max-w-xs">{r.summary}</p>
                              <p className="text-gray-400 text-[10px]">{r.assignee?`OWNER: ${r.assignee}`:""}{r.assignee&&r.wbs_code?" · ":""}{r.wbs_code?`WBS: ${r.wbs_code}`:""}</p>
                              <p className="text-blue-600 font-mono text-[10px]">{r.issue_key}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-2 text-center">{!r.impact_index?"—":(() => {
                          const lvl = r.impact_index.replace(/^\(\d+\)\s*/,"");
                          return <span className={`px-2 py-0.5 rounded-full text-xs font-medium border bg-white ${lvl.includes("Severe")?"text-red-600 border-red-300":lvl.includes("Significant")?"text-orange-600 border-orange-300":"text-green-600 border-green-300"}`}>{lvl}</span>;
                        })()}</td>
                        <td className="py-3 px-2 text-center">{(() => {
                          const p = r.probability_pct;
                          if(p==null) return "—";
                          const cls = p>=70?"text-red-600 font-semibold":p>=40?"text-orange-500 font-semibold":"text-gray-600";
                          return <span className={cls}>{p}%</span>;
                        })()}</td>
                        <td className="py-3 px-2 text-center">{(() => {
                          const v = r.factored_cost_k;
                          if(v==null) return "—";
                          const cls = v>=1000?"text-red-600 font-semibold":v>=500?"text-orange-500 font-semibold":"text-gray-600";
                          return <span className={cls}>${v.toLocaleString()}K</span>;
                        })()}</td>
                        <td className="py-3 px-2 text-center"><span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${r.trending==="Increasing"?"bg-red-50 text-red-600 border-red-200":r.trending==="Decreasing"?"bg-green-50 text-green-600 border-green-200":"bg-orange-50 text-orange-600 border-orange-200"}`}>{r.trending==="Increasing"?"↑ Increasing":r.trending==="Decreasing"?"↓ Decreasing":"— Neutral"}</span></td>
                        <td className="py-3 px-2 text-center"><span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${mitigationCls[r.status]||"bg-gray-100 text-gray-600 border-gray-200"}`}>{r.mitigation_plan?r.mitigation_plan:(mitigationCls[r.status]?r.status:"—")}</span></td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
                <div className="flex justify-between mt-3 pt-3 border-t border-gray-100 text-xs text-gray-500">
                  <span>Showing {Math.min(8,jiraRisks.length)} active risks</span>
                  <span>Total Exposure: ${jiraRisks.reduce((s:number,r:any)=>s+(r.cost_impact_k||0),0).toLocaleString()}K · Factored: ${jiraRisks.reduce((s:number,r:any)=>s+(r.factored_cost_k||0),0).toLocaleString()}K</span>
                </div>
              </div>
            )}

            {/* Customer Summary */}
            {(() => {
              const sentimentStyle = (s: string) =>
                s === "POSITIVE" ? "bg-green-50 text-green-700 border-green-200"
                : s === "NEGATIVE" ? "bg-red-50 text-red-700 border-red-200"
                : "bg-gray-50 text-gray-600 border-gray-200";
              const cardBorder = (s: string) =>
                s === "POSITIVE" ? "border-green-200 bg-green-50/30"
                : s === "NEGATIVE" ? "border-red-200 bg-red-50/30"
                : "border-gray-200";
              const allSelected = custFeedbacks.length > 0 && custSelected.size === custFeedbacks.length;
              const openAdd = () => { setCustForm({ name:"", role:"", feedback:"", sentiment:"NEUTRAL", used_in_ai:false, flagged:false }); setCustEditing(null); setCustModal("add"); };
              const openEdit = (f: any) => { setCustForm({ name:f.name, role:f.role||"", feedback:f.feedback, sentiment:f.sentiment, used_in_ai:f.used_in_ai, flagged:f.flagged }); setCustEditing(f); setCustModal("edit"); };
              const saveForm = async () => {
                if (!custForm.name.trim() || !custForm.feedback.trim()) return;
                if (custModal === "add") {
                  const r = await api.post("/api/customer-feedback", { ...custForm, report_id: reportId });
                  setCustFeedbacks(prev => [r.data, ...prev]);
                } else if (custModal === "edit" && custEditing) {
                  const r = await api.patch(`/api/customer-feedback/${custEditing.id}`, custForm);
                  setCustFeedbacks(prev => prev.map(f => f.id === custEditing.id ? r.data : f));
                }
                setCustModal(null);
              };
              const deleteSingle = async (id: string) => {
                await api.delete(`/api/customer-feedback/${id}`);
                setCustFeedbacks(prev => prev.filter(f => f.id !== id));
                setCustSelected(prev => { const s = new Set(prev); s.delete(id); return s; });
              };
              const deleteSelected = async () => {
                const ids = Array.from(custSelected);
                await api.delete("/api/customer-feedback", { data: { ids } });
                setCustFeedbacks(prev => prev.filter(f => !custSelected.has(f.id)));
                setCustSelected(new Set());
              };

              return (
                <div className="bg-white rounded-xl border border-gray-200 p-5">
                  <div className="flex items-center justify-between mb-4">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Customer Summary</p>
                    <div className="flex items-center gap-2">
                      {custFeedbacks.length > 0 && <span className="text-xs text-blue-600 border border-blue-200 rounded-full px-2.5 py-1 font-medium">{custFeedbacks.length} Feedback {custFeedbacks.length === 1 ? "Entry" : "Entries"}</span>}
                      <button onClick={openAdd} className="text-xs bg-blue-600 text-white px-3 py-1.5 rounded-lg hover:bg-blue-700 font-medium">+ Add Feedback</button>
                    </div>
                  </div>

                  {custFeedbacks.length > 0 ? (
                    <div className="space-y-3">
                      {custFeedbacks.map((f: any) => (
                        <div key={f.id} className={`border rounded-xl p-4 ${cardBorder(f.sentiment)}`}>
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <div className="flex items-center gap-2.5">
                              <input type="checkbox" className="w-3.5 h-3.5 rounded border-gray-300 mt-0.5 flex-shrink-0"
                                checked={custSelected.has(f.id)}
                                onChange={e => setCustSelected(prev => { const s = new Set(prev); e.target.checked ? s.add(f.id) : s.delete(f.id); return s; })} />
                              <div>
                                <div className="flex items-center gap-2">
                                  <span className="text-xs font-semibold text-gray-900">{f.name}</span>
                                  {f.role && <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">{f.role}</span>}
                                </div>
                                <span className="text-xs text-gray-400">Submitted on {new Date(f.created_at).toLocaleDateString("en-US", { month:"short", day:"numeric", year:"numeric" })}</span>
                              </div>
                            </div>
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium border flex-shrink-0 ${sentimentStyle(f.sentiment)}`}>{f.sentiment}</span>
                          </div>

                          <p className="text-xs text-gray-700 leading-relaxed mb-3 ml-6">{f.feedback}</p>

                          <div className="flex items-center justify-between ml-6">
                            <div className="flex gap-2">
                              {f.used_in_ai && <span className="text-xs text-blue-600 border border-blue-200 rounded-md px-2 py-0.5">Used in AI Report Generation</span>}
                              {f.flagged && <span className="text-xs text-red-600 border border-red-200 rounded-md px-2 py-0.5">Flagged for Executive Attention</span>}
                            </div>
                            <div className="flex gap-1.5">
                              <button onClick={() => openEdit(f)} className="text-xs border border-blue-200 text-blue-600 rounded-md px-2.5 py-1 hover:bg-blue-50">✎ Edit Feedback</button>
                              <button onClick={() => deleteSingle(f.id)} className="text-xs border border-red-200 text-red-500 rounded-md px-2.5 py-1 hover:bg-red-50">🗑 Delete</button>
                            </div>
                          </div>
                        </div>
                      ))}

                      {/* Selection bar */}
                      <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                        <span className="text-xs text-gray-500">
                          {custSelected.size > 0 ? `${custSelected.size} FEEDBACK ${custSelected.size === 1 ? "ENTRY" : "ENTRIES"} SELECTED` : ""}
                        </span>
                        <div className="flex gap-2">
                          <button onClick={() => setCustSelected(allSelected ? new Set() : new Set(custFeedbacks.map((f:any)=>f.id)))}
                            className="text-xs border border-gray-200 text-gray-600 rounded-md px-2.5 py-1 hover:bg-gray-50">
                            {allSelected ? "Deselect All" : "Select All"}
                          </button>
                          {custSelected.size > 0 && (
                            <button onClick={deleteSelected} className="text-xs border border-red-200 text-red-500 rounded-md px-2.5 py-1 hover:bg-red-50">
                              🗑 Delete Selected
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <p className="text-xs text-gray-400 py-4 text-center">No customer feedback yet. Click "+ Add Feedback" to log the first entry.</p>
                  )}

                  {/* Add / Edit Modal */}
                  {custModal && (
                    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setCustModal(null)}>
                      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                          <p className="text-sm font-semibold text-gray-900">{custModal === "add" ? "Add Customer Feedback" : "Edit Feedback"}</p>
                          <button onClick={() => setCustModal(null)} className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-400 text-lg">×</button>
                        </div>
                        <div className="px-5 py-4 space-y-3">
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="text-xs text-gray-500 font-medium block mb-1">Name *</label>
                              <input value={custForm.name} onChange={e => setCustForm(p=>({...p,name:e.target.value}))}
                                className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-300" placeholder="e.g. David Anderson" />
                            </div>
                            <div>
                              <label className="text-xs text-gray-500 font-medium block mb-1">Role</label>
                              <input value={custForm.role} onChange={e => setCustForm(p=>({...p,role:e.target.value}))}
                                className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-300" placeholder="e.g. Program Manager" />
                            </div>
                          </div>
                          <div>
                            <label className="text-xs text-gray-500 font-medium block mb-1">Feedback *</label>
                            <textarea value={custForm.feedback} onChange={e => setCustForm(p=>({...p,feedback:e.target.value}))} rows={4}
                              className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-300 resize-none" placeholder="Customer's feedback..." />
                          </div>
                          <div>
                            <label className="text-xs text-gray-500 font-medium block mb-1">Sentiment</label>
                            <div className="flex gap-2">
                              {["POSITIVE","NEUTRAL","NEGATIVE"].map(s => (
                                <button key={s} onClick={() => setCustForm(p=>({...p,sentiment:s}))}
                                  className={`text-xs px-3 py-1.5 rounded-full border font-medium transition-colors ${custForm.sentiment===s ? sentimentStyle(s) : "border-gray-200 text-gray-500 hover:bg-gray-50"}`}>{s}</button>
                              ))}
                            </div>
                          </div>
                          <div className="flex gap-4">
                            <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
                              <input type="checkbox" checked={custForm.used_in_ai} onChange={e=>setCustForm(p=>({...p,used_in_ai:e.target.checked}))} className="w-3.5 h-3.5 rounded" />
                              Used in AI Report Generation
                            </label>
                            <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
                              <input type="checkbox" checked={custForm.flagged} onChange={e=>setCustForm(p=>({...p,flagged:e.target.checked}))} className="w-3.5 h-3.5 rounded" />
                              Flag for Executive Attention
                            </label>
                          </div>
                        </div>
                        <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-100">
                          <button onClick={() => setCustModal(null)} className="text-sm px-4 py-2 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50">Cancel</button>
                          <button onClick={saveForm} disabled={!custForm.name.trim() || !custForm.feedback.trim()}
                            className="text-sm px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium">
                            {custModal === "add" ? "Add Feedback" : "Save Changes"}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        )}

        {/* PERFORMANCE */}
        {tab==="performance"&&(
          <div className="space-y-4">
            <div className="grid grid-cols-4 gap-3">
              {[{label:"CPI",value:performance?.cpi?.toFixed(3),color:cpiColor(performance?.cpi),sub:"Cost Performance Index"},{label:"SPI",value:performance?.spi?.toFixed(3),color:cpiColor(performance?.spi),sub:"Schedule Performance Index"},{label:"Cost Variance",value:fmt((performance?.eac||0)-(performance?.bac||0)),color:varColor((performance?.eac||0)-(performance?.bac||0)),sub:"EAC vs BAC"},{label:"EAC",value:fmt(performance?.eac),color:"text-gray-900",sub:"Estimate at Completion"}].map(m=>(
                <div key={m.label} className="bg-white rounded-xl border border-gray-200 p-4">
                  <p className="text-xs text-gray-400 mb-1">{m.label}</p>
                  <p className={`text-2xl font-bold ${m.color}`}>{m.value||"—"}</p>
                  <p className="text-xs text-gray-400 mt-1">{m.sub}</p>
                </div>
              ))}
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <p className="text-sm font-semibold text-gray-900 mb-4">Baseline vs Forecast Dates — Control Accounts</p>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50"><tr>{["Dept","Description","Baseline Finish","Forecast Finish","Slip"].map(h=><th key={h} className="text-left px-3 py-2 font-medium text-gray-500 uppercase">{h}</th>)}</tr></thead>
                  <tbody className="divide-y divide-gray-100">
                    {(performance?.schedule_comparison||[]).map((row:any,i:number)=>{
                      const bf=row.baseline_finish?new Date(row.baseline_finish):null;
                      const ff=row.forecast_finish?new Date(row.forecast_finish):null;
                      const slip=bf&&ff?Math.round((ff.getTime()-bf.getTime())/86400000):null;
                      return(<tr key={i} className="hover:bg-gray-50">
                        <td className="px-3 py-2"><span className="font-mono bg-blue-50 text-blue-700 px-1.5 rounded text-xs">{row.obs}</span></td>
                        <td className="px-3 py-2 text-xs text-gray-700 max-w-xs truncate">{row.description}</td>
                        <td className="px-3 py-2 text-xs text-gray-500">{row.baseline_finish?.split("T")[0]}</td>
                        <td className="px-3 py-2 text-xs">{row.forecast_finish?.split("T")[0]}</td>
                        <td className="px-3 py-2 text-xs font-medium">{slip!==null?<span className={slip>0?"text-red-500":"text-green-600"}>{slip>0?`+${slip}d`:slip===0?"On time":`${slip}d`}</span>:"—"}</td>
                      </tr>);
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* FINANCIALS */}
        {tab==="financials"&&(
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <p className="text-sm font-semibold text-gray-900 mb-4">Financial Summary by Department</p>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-50"><tr>{["Dept","Description","CAM","ACWP","ETC","EAC","BAC","VAC","CPI"].map(h=><th key={h} className={`px-3 py-2 font-medium text-gray-500 uppercase ${h!=="Dept"&&h!=="Description"&&h!=="CAM"?"text-right":"text-left"}`}>{h}</th>)}</tr></thead>
                <tbody className="divide-y divide-gray-100">
                  {(financials?.summary_table||[]).map((row:any,i:number)=>(
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-3 py-2"><span className="font-mono bg-blue-50 text-blue-700 px-1.5 rounded text-xs">{row.obs}</span></td>
                      <td className="px-3 py-2 text-xs text-gray-700 max-w-[150px] truncate">{row.description}</td>
                      <td className="px-3 py-2 text-xs text-gray-500 whitespace-nowrap">{row.cam}</td>
                      <td className="px-3 py-2 text-xs text-right">{fmt(row.acwp)}</td>
                      <td className="px-3 py-2 text-xs text-right">{fmt(row.etc)}</td>
                      <td className="px-3 py-2 text-xs text-right font-medium">{fmt(row.eac)}</td>
                      <td className="px-3 py-2 text-xs text-right">{fmt(row.bac)}</td>
                      <td className={`px-3 py-2 text-xs text-right font-medium ${varColor(row.vac)}`}>{fmt(row.vac)}</td>
                      <td className={`px-3 py-2 text-xs text-right font-bold ${cpiColor(row.cpi)}`}>{row.cpi?.toFixed(3)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* SCHEDULE */}
        {tab==="schedule"&&(
          <div className="space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-lg">📋</span>
                <p className="text-sm font-medium text-amber-800">IMS Integration Pending</p>
              </div>
              <p className="text-xs text-amber-700">Milestone data is maintained in MS Project IMS. Full milestone register will be available once the IMS file is delivered by Collins (contact: Tony).</p>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <p className="text-sm font-semibold text-gray-900 mb-4">Remaining Scope</p>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50"><tr>{["Dept","Description","Status","Forecast Finish","Baseline Finish","On Time"].map(h=><th key={h} className="text-left px-3 py-2 font-medium text-gray-500 uppercase">{h}</th>)}</tr></thead>
                  <tbody className="divide-y divide-gray-100">
                    {(schedule?.remaining_scope||[]).slice(0,15).map((row:any,i:number)=>{
                      const onTimeCell = row.on_time===true ? <span className="text-green-600">✓ On time</span> : row.on_time===false ? <span className="text-red-500">⚠ Late</span> : <span>—</span>;
                      return (
                      <tr key={i} className="hover:bg-gray-50">
                        <td className="px-3 py-2"><span className="font-mono bg-blue-50 text-blue-700 px-1.5 rounded">{row.obs}</span></td>
                        <td className="px-3 py-2 text-gray-700 max-w-xs truncate">{row.description}</td>
                        <td className="px-3 py-2 text-gray-500">{row.status}</td>
                        <td className="px-3 py-2">{row.forecast_finish}</td>
                        <td className="px-3 py-2 text-gray-500">{row.baseline_finish||"—"}</td>
                        <td className="px-3 py-2 font-medium">{onTimeCell}</td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* STAFFING */}
        {tab==="staffing"&&(
          <div className="space-y-4">
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <p className="text-sm font-semibold text-gray-900 mb-4">FTE Chart Over Time</p>
              {staffing?.fte_chart?.length>0?(
                <ResponsiveContainer width="100%" height={280}>
                  <AreaChart data={staffing.fte_chart}>
                    <XAxis dataKey="period" tick={{fontSize:9}} interval={2}/><YAxis tick={{fontSize:10}} unit=" FTE"/><Tooltip/><Legend/>
                    {(staffing.work_centers||[]).slice(0,6).map((wc:string,i:number)=>{
                      const colors=["#3b82f6","#ef4444","#22c55e","#f59e0b","#8b5cf6","#ec4899"];
                      return <Area key={wc} type="monotone" dataKey={wc} stackId="1" stroke={colors[i]} fill={colors[i]} fillOpacity={0.6} name={wc}/>;
                    })}
                  </AreaChart>
                </ResponsiveContainer>
              ):<p className="text-sm text-gray-400">No staffing data available.</p>}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <p className="text-sm font-semibold text-gray-900 mb-4">Breakdown by Manager</p>
                <div className="space-y-2">{(staffing?.by_manager||[]).map((row:any,i:number)=>(
                  <div key={i} className="flex items-center justify-between py-1.5 border-b border-gray-50 last:border-0">
                    <span className="text-xs text-gray-700">{row.Emp_Name}</span><span className="text-sm font-medium">{row.fte} FTE</span>
                  </div>
                ))}</div>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <p className="text-sm font-semibold text-gray-900 mb-4">Plan vs Actual Hours</p>
                {staffing?.plan_vs_actual?.length>0?(
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={staffing.plan_vs_actual} layout="vertical">
                      <XAxis type="number" tick={{fontSize:10}}/><YAxis dataKey="work_center" type="category" tick={{fontSize:9}} width={70}/><Tooltip/><Legend/>
                      <Bar dataKey="plan_hrs" fill="#3b82f6" name="Plan hrs"/><Bar dataKey="actual_hrs" fill="#ef4444" name="Actual hrs"/>
                    </BarChart>
                  </ResponsiveContainer>
                ):<p className="text-sm text-gray-400">No plan vs actual data.</p>}
              </div>
            </div>
          </div>
        )}

        {/* RISKS */}
        {tab==="risks"&&(
          <div className="space-y-4">
            {!jiraAvailable?(
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-8 text-center">
                <p className="text-amber-700 font-medium mb-1">Jira Risk Register</p>
                <p className="text-amber-600 text-sm">Upload Jira R&O file when generating a report to populate this section.</p>
              </div>
            ):(
              <>
                <div className="grid grid-cols-3 gap-4">
                  {[{label:"Total Risks",value:jiraRisks.length,color:"text-gray-900"},{label:"Active Risks",value:jiraRisks.filter((r:any)=>r.status==="Active").length,color:"text-red-600"},{label:"Total Factored Cost",value:`$${(jiraRisks.reduce((s:number,r:any)=>s+(r.factored_cost_k||0),0)/1000).toFixed(1)}M`,color:"text-gray-900"}].map(m=>(
                    <div key={m.label} className="bg-white rounded-xl border border-gray-200 p-5"><p className="text-xs text-gray-400 mb-1">{m.label}</p><p className={`text-2xl font-bold ${m.color}`}>{m.value}</p></div>
                  ))}
                </div>
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  <div className="px-5 py-4 border-b border-gray-100"><p className="text-sm font-semibold text-gray-900">Risk Register — from Jira R&O</p></div>
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50"><tr>{["Issue","Summary","Status","Prob %","Cost Impact ($K)","Factored ($K)","Area","Trending"].map(h=><th key={h} className="px-4 py-3 text-left font-medium text-gray-500">{h}</th>)}</tr></thead>
                    <tbody className="divide-y divide-gray-50">
                      {jiraRisks.map((r:any,i:number)=>(
                        <tr key={i} className="hover:bg-gray-50">
                          <td className="px-4 py-3 font-mono text-blue-600">{r.issue_key}</td>
                          <td className="px-4 py-3 max-w-xs truncate">{r.summary}</td>
                          <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full font-medium ${r.status==="Active"?"bg-red-50 text-red-600":r.status==="Draft"?"bg-yellow-50 text-yellow-600":"bg-gray-100 text-gray-500"}`}>{r.status}</span></td>
                          <td className="px-4 py-3 text-right">{r.probability_pct?`${r.probability_pct}%`:"—"}</td>
                          <td className="px-4 py-3 text-right">{r.cost_impact_k?`$${r.cost_impact_k.toLocaleString()}`:"—"}</td>
                          <td className="px-4 py-3 text-right font-medium">{r.factored_cost_k?`$${r.factored_cost_k.toLocaleString()}`:"—"}</td>
                          <td className="px-4 py-3 text-gray-500">{r.domain_category||"—"}</td>
                          <td className="px-4 py-3 text-gray-500">{r.trending||"—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}

        {/* VARIANCES */}
        {tab==="variances"&&(
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100">
              <p className="text-sm font-semibold text-gray-900">Key Variances — {variances.length} items</p>
              <p className="text-xs text-gray-400 mt-0.5">Ranked by EAC change magnitude — click any row to expand</p>
            </div>
            <table className="w-full">
              <thead className="bg-gray-50"><tr>{["#","Dept","Description","EAC Change","CPI","VAC","Direction",""].map(h=><th key={h} className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">{h}</th>)}</tr></thead>
              <tbody>{variances.map((v:any,i:number)=><VarianceRow key={i} v={v} i={i}/>)}</tbody>
            </table>
          </div>
        )}

        {/* MILESTONES */}
        {tab==="milestones"&&(
          <div className="space-y-4">
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <p className="text-sm font-semibold text-gray-900 mb-4">Program Milestones</p>
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <span className="text-5xl mb-4">📋</span>
                <p className="text-base font-semibold text-gray-700 mb-2">IMS Integration Pending</p>
                <p className="text-sm text-gray-400 max-w-md">Collins confirmed milestones are maintained in MS Project IMS. Full milestone register will be available once the IMS file is delivered by Collins (contact: Tony).</p>
              </div>
            </div>
          </div>
        )}

        {/* WORK PACKAGES */}

      </div>

      {/* Audit Trail Modal */}
      {auditOpen && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setAuditOpen(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <div>
                <p className="text-sm font-semibold text-gray-900">Audit Trail</p>
                <p className="text-xs text-gray-400 mt-0.5">All edits made to this report</p>
              </div>
              <button onClick={() => setAuditOpen(false)} className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-400 text-lg">×</button>
            </div>

            <div className="overflow-y-auto flex-1 px-5 py-3 space-y-4">
              {auditLoading ? (
                <p className="text-sm text-gray-400 text-center py-8">Loading…</p>
              ) : auditTrail.length === 0 ? (
                <div className="text-center py-10">
                  <p className="text-sm text-gray-400">No edits recorded yet.</p>
                  <p className="text-xs text-gray-300 mt-1">Edits to the summary and other fields will appear here.</p>
                </div>
              ) : auditTrail.map((entry: any) => {
                const date = new Date(entry.created_at);
                const diff = Date.now() - date.getTime();
                const rel = diff < 60000 ? "just now" : diff < 3600000 ? `${Math.floor(diff/60000)}m ago` : diff < 86400000 ? `${Math.floor(diff/3600000)}h ago` : date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
                const actionLabel: Record<string, string> = { edit_summary: "Edited summary" };
                const label = actionLabel[entry.action] || entry.action.replace(/_/g, " ");

                const before: string[] = Array.isArray(entry.before_val) ? entry.before_val : [];
                const after: string[]  = Array.isArray(entry.after_val)  ? entry.after_val  : [];
                const removed = before.filter((b: string) => !after.includes(b));
                const added   = after.filter((a: string)  => !before.includes(a));

                return (
                  <div key={entry.id} className="border border-gray-100 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center text-xs font-bold text-blue-600">
                          {entry.user_name?.[0]?.toUpperCase() || "?"}
                        </div>
                        <div>
                          <span className="text-xs font-semibold text-gray-800">{entry.user_name}</span>
                          <span className="text-xs text-gray-400 ml-1.5">{label}</span>
                        </div>
                      </div>
                      <span className="text-xs text-gray-400">{rel}</span>
                    </div>

                    {(removed.length > 0 || added.length > 0) && (
                      <div className="mt-2 space-y-1.5 text-xs">
                        {removed.map((line: string, i: number) => (
                          <div key={`r${i}`} className="flex items-start gap-1.5 bg-red-50 rounded px-2 py-1 text-red-700">
                            <span className="font-bold flex-shrink-0">−</span>
                            <span className="line-through opacity-70">{line}</span>
                          </div>
                        ))}
                        {added.map((line: string, i: number) => (
                          <div key={`a${i}`} className="flex items-start gap-1.5 bg-green-50 rounded px-2 py-1 text-green-700">
                            <span className="font-bold flex-shrink-0">+</span>
                            <span>{line}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
